import { createSlice } from '@reduxjs/toolkit';
import queryString from 'query-string';
import { REDUX_ASYNC_STATUSES } from '../../consts/consts';

const DEFAULT_REPORTING_FILTERS = 'insured_risk_type=building&insured_risk_type=glass';
const DEFAULT_REPORTING_RISK_OUTLIERS_FILTERS = 'sort=-num_claims';
const { IDLE, LOADING, SUCCEEDED, FAILED } = REDUX_ASYNC_STATUSES;

const initialState = {
  claimsActiveFilters: '',
  risksActiveFilters: '',
  policiesActiveFilters: '',
  reportingActiveFilters: DEFAULT_REPORTING_FILTERS,
  reportingRiskOutliersSortingParameter: DEFAULT_REPORTING_RISK_OUTLIERS_FILTERS,
  risksSortingParameter: undefined,
  isFiltersDrawerOpen: false,

  claimsHousingStocksFilterOptions: [],
  getClaimsHousingStocksFilterOptionsStatus: IDLE,

  claimsInsuredRisksFilterOptions: [],
  claimsInsuredRisksFilterOptionsStatus: IDLE,

  claimsInsuredRiskTypesFilterOptions: [],
  claimsInsuredRiskTypesFilterOptionsStatus: IDLE,

  claimsCreatedByOrganizationsFilterOptions: [],
  claimsCreatedByOrganizationsFilterOptionsStatus: IDLE,

  claimsOrganizationsWithAccessFilterOptions: [],
  claimsOrganizationsWithAccessFilterOptionsStatus: IDLE,

  policiesHousingStocksFilterOptions: [],
  getPoliciesHousingStocksFilterOptionsStatus: IDLE,

  policiesInsuranceOrganizationsFilterOptions: [],
  policiesInsuranceOrganizationsFilterOptionsStatus: IDLE,

  policiesCustomFieldsFilterOptions: [],
  policiesCustomFieldsFilterOptionsStatus: IDLE,

  policiesInsuredRiskTypesFilterOptions: [],
  policiesInsuredRiskTypesFilterOptionsStatus: IDLE,

  policiesInsuredRisksFilterOptions: [],
  policiesInsuredRisksFilterOptionsStatus: IDLE,

  risksHousingStocksFilterOptions: [],
  risksHousingStocksFilterOptionsStatus: IDLE,

  risksOrganizationsFilterOptions: [],
  risksOrganizationsFilterOptionsStatus: IDLE,

  risksCompanyLabelsFilterOptions: [],
  risksCompanyLabelsFilterOptionsStatus: IDLE,

  risksCompanyCodesFilterOptions: [],
  risksCompanyCodesFilterOptionsStatus: IDLE,

  risksEconomicUnitsFilterOptions: [],
  risksEconomicUnitsFilterOptionsStatus: IDLE
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setClaimsFilters: (state, action) => {
      state.claimsActiveFilters = queryString.stringify(action.payload);
    },
    setRisksFilters: (state, action) => {
      state.risksActiveFilters = queryString.stringify(action.payload);
    },
    setPoliciesFilters: (state, action) => {
      state.policiesActiveFilters = queryString.stringify(action.payload);
    },
    setReportingFilters: (state, action) => {
      state.reportingActiveFilters = queryString.stringify(action.payload);
    },
    setReportingRiskOutliersSortingParameter: (state, action) => {
      state.reportingRiskOutliersSortingParameter = queryString.stringify(action.payload);
    },
    setRisksSortingParameter: (state, action) => {
      state.risksSortingParameter = action.payload;
    },
    showFiltersDrawer: (state) => {
      state.isFiltersDrawerOpen = true;
    },
    hideFiltersDrawer: (state) => {
      state.isFiltersDrawerOpen = false;
    },

    getClaimsHousingStocksFilterOptions(state) {
      state.getClaimsHousingStocksFilterOptionsStatus = LOADING;
    },
    getClaimsHousingStocksFilterOptionsSucceeded(state, action) {
      state.claimsHousingStocksFilterOptions = action.payload;
      state.getClaimsHousingStocksFilterOptionsStatus = SUCCEEDED;
    },
    getClaimsHousingStocksFilterOptionsFailed(state) {
      state.getClaimsHousingStocksFilterOptionsStatus = FAILED;
    },

    getClaimsInsuredRisksFilterOptions: (state) => {
      state.claimsInsuredRisksFilterOptionsStatus = LOADING;
    },
    getClaimsInsuredRisksSucceeded: (state, action) => {
      state.claimsInsuredRisksFilterOptionsStatus = SUCCEEDED;
      state.claimsInsuredRisksFilterOptions = action.payload;
    },
    getClaimsInsuredRisksFailed: (state) => {
      state.claimsInsuredRisksFilterOptionsStatus = FAILED;
    },

    getClaimsInsuredRiskTypesFilterOptions(state) {
      state.claimsInsuredRiskTypesFilterOptionsStatus = LOADING;
    },
    getClaimsInsuredRiskTypesFilterOptionsSucceeded(state, action) {
      state.claimsInsuredRiskTypesFilterOptions = action.payload;
      state.claimsInsuredRiskTypesFilterOptionsStatus = SUCCEEDED;
    },
    getClaimsInsuredRiskTypesFilterOptionsFailed(state) {
      state.claimsInsuredRiskTypesFilterOptionsStatus = FAILED;
    },

    getClaimsCreatedByOrganizationsFilterOptions(state) {
      state.claimsCreatedByOrganizationsFilterOptionsStatus = LOADING;
    },
    getClaimsCreatedByOrganizationsFilterOptionsSucceeded(state, action) {
      state.claimsCreatedByOrganizationsFilterOptions = action.payload;
      state.claimsCreatedByOrganizationsFilterOptionsStatus = SUCCEEDED;
    },
    getClaimsCreatedByOrganizationsFilterOptionsFailed(state) {
      state.claimsCreatedByOrganizationsFilterOptionsStatus = FAILED;
    },

    getClaimsOrganizationsWithAccessFilterOptions(state) {
      state.claimsOrganizationsWithAccessFilterOptionsStatus = LOADING;
    },
    getClaimsOrganizationsWithAccessFilterOptionsSucceeded(state, action) {
      state.claimsOrganizationsWithAccessFilterOptions = action.payload;
      state.claimsOrganizationsWithAccessFilterOptionsStatus = SUCCEEDED;
    },
    getClaimsOrganizationsWithAccessFilterOptionsFailed(state) {
      state.claimsOrganizationsWithAccessFilterOptionsStatus = FAILED;
    },

    getPoliciesHousingStocksFilterOptions(state) {
      state.getPoliciesHousingStocksFilterOptionsStatus = LOADING;
    },
    getPoliciesHousingStocksFilterOptionsSucceeded(state, action) {
      state.policiesHousingStocksFilterOptions = action.payload;
      state.getPoliciesHousingStocksFilterOptionsStatus = SUCCEEDED;
    },
    getPoliciesHousingStocksFilterOptionsFailed(state) {
      state.getPoliciesHousingStocksFilterOptionsStatus = FAILED;
    },

    getPoliciesInsuranceOrganizationsFilterOptions(state) {
      state.policiesInsuranceOrganizationsFilterOptionsStatus = LOADING;
    },
    getPoliciesInsuranceOrganizationsFilterOptionsSucceeded(state, action) {
      state.policiesInsuranceOrganizationsFilterOptions = action.payload;
      state.policiesInsuranceOrganizationsFilterOptionsStatus = SUCCEEDED;
    },
    getPoliciesInsuranceOrganizationsFilterOptionsFailed(state) {
      state.policiesInsuranceOrganizationsFilterOptionsStatus = FAILED;
    },

    getPoliciesCustomFieldsFilterOptions(state) {
      state.policiesCustomFieldsFilterOptionsStatus = LOADING;
    },
    getPoliciesCustomFieldsFilterOptionsSucceeded(state, action) {
      state.policiesCustomFieldsFilterOptions = action.payload;
      state.policiesCustomFieldsFilterOptionsStatus = SUCCEEDED;
    },
    getPoliciesCustomFieldsFilterOptionsFailed(state) {
      state.policiesCustomFieldsFilterOptionsStatus = FAILED;
    },

    getPoliciesInsuredRiskTypesFilterOptions(state) {
      state.policiesInsuredRiskTypesFilterOptionsStatus = LOADING;
    },
    getPoliciesInsuredRiskTypesFilterOptionsSucceeded(state, action) {
      state.policiesInsuredRiskTypesFilterOptions = action.payload;
      state.policiesInsuredRiskTypesFilterOptionsStatus = SUCCEEDED;
    },
    getPoliciesInsuredRiskTypesFilterOptionsFailed(state) {
      state.policiesInsuredRiskTypesFilterOptionsStatus = FAILED;
    },

    getPoliciesInsuredRisksFilterOptions(state) {
      state.policiesInsuredRisksFilterOptionsStatus = LOADING;
    },
    getPoliciesInsuredRisksFilterOptionsSucceeded(state, action) {
      state.policiesInsuredRisksFilterOptions = action.payload;
      state.policiesInsuredRisksFilterOptionsStatus = SUCCEEDED;
    },
    getPoliciesInsuredRisksFilterOptionsFailed(state) {
      state.policiesInsuredRisksFilterOptionsStatus = FAILED;
    },

    getRisksHousingStocksFilterOptions(state) {
      state.risksHousingStocksFilterOptionsStatus = LOADING;
    },
    getRisksHousingStocksFilterOptionsSucceeded(state, action) {
      state.risksHousingStocksFilterOptions = action.payload;
      state.risksHousingStocksFilterOptionsStatus = SUCCEEDED;
    },
    getRisksHousingStocksFilterOptionsFailed(state) {
      state.risksHousingStocksFilterOptionsStatus = FAILED;
    },

    getRisksOrganizationsFilterOptions(state) {
      state.risksOrganizationsFilterOptionsStatus = LOADING;
    },
    getRisksOrganizationsFilterOptionsSucceeded(state, action) {
      state.risksOrganizationsFilterOptions = action.payload;
      state.risksOrganizationsFilterOptionsStatus = SUCCEEDED;
    },
    getRisksOrganizationsFilterOptionsFailed(state) {
      state.risksOrganizationsFilterOptionsStatus = FAILED;
    },

    getRisksCompanyLabelsFilterOptions(state) {
      state.risksCompanyLabelsFilterOptionsStatus = LOADING;
    },
    getRisksCompanyLabelsFilterOptionsSucceeded(state, action) {
      state.risksCompanyLabelsFilterOptions = action.payload;
      state.risksCompanyLabelsFilterOptionsStatus = SUCCEEDED;
    },
    getRisksCompanyLabelsFilterOptionsFailed(state) {
      state.risksCompanyLabelsFilterOptionsStatus = FAILED;
    },

    getRisksCompanyCodesFilterOptions(state) {
      state.risksCompanyCodesFilterOptionsStatus = LOADING;
    },
    getRisksCompanyCodesFilterOptionsSucceeded(state, action) {
      state.risksCompanyCodesFilterOptions = action.payload;
      state.risksCompanyCodesFilterOptionsStatus = SUCCEEDED;
    },
    getRisksCompanyCodesFilterOptionsFailed(state) {
      state.risksCompanyCodesFilterOptionsStatus = FAILED;
    },

    getRisksEconomicUnitsFilterOptions(state) {
      state.risksEconomicUnitsFilterOptionsStatus = LOADING;
    },
    getRisksEconomicUnitsFilterOptionsSucceeded(state, action) {
      state.risksEconomicUnitsFilterOptions = action.payload;
      state.risksEconomicUnitsFilterOptionsStatus = SUCCEEDED;
    },
    getRisksEconomicUnitsFilterOptionsFailed(state) {
      state.risksEconomicUnitsFilterOptionsStatus = FAILED;
    }
  }
});

export const {
  setClaimsFilters,
  setRisksFilters,
  setPoliciesFilters,
  setReportingFilters,
  setReportingRiskOutliersSortingParameter,
  setRisksSortingParameter,
  showFiltersDrawer,
  hideFiltersDrawer,

  getClaimsHousingStocksFilterOptions,
  getClaimsHousingStocksFilterOptionsSucceeded,
  getClaimsHousingStocksFilterOptionsFailed,

  getClaimsInsuredRisksFilterOptions,
  getClaimsInsuredRisksSucceeded,
  getClaimsInsuredRisksFailed,

  getClaimsInsuredRiskTypesFilterOptions,
  getClaimsInsuredRiskTypesFilterOptionsSucceeded,
  getClaimsInsuredRiskTypesFilterOptionsFailed,

  getClaimsCreatedByOrganizationsFilterOptions,
  getClaimsCreatedByOrganizationsFilterOptionsSucceeded,
  getClaimsCreatedByOrganizationsFilterOptionsFailed,

  getClaimsOrganizationsWithAccessFilterOptions,
  getClaimsOrganizationsWithAccessFilterOptionsSucceeded,
  getClaimsOrganizationsWithAccessFilterOptionsFailed,

  getPoliciesHousingStocksFilterOptions,
  getPoliciesHousingStocksFilterOptionsSucceeded,
  getPoliciesHousingStocksFilterOptionsFailed,

  getPoliciesInsuranceOrganizationsFilterOptions,
  getPoliciesInsuranceOrganizationsFilterOptionsSucceeded,
  getPoliciesInsuranceOrganizationsFilterOptionsFailed,

  getPoliciesCustomFieldsFilterOptions,
  getPoliciesCustomFieldsFilterOptionsSucceeded,
  getPoliciesCustomFieldsFilterOptionsFailed,

  getPoliciesInsuredRiskTypesFilterOptions,
  getPoliciesInsuredRiskTypesFilterOptionsSucceeded,
  getPoliciesInsuredRiskTypesFilterOptionsFailed,

  getPoliciesInsuredRisksFilterOptions,
  getPoliciesInsuredRisksFilterOptionsSucceeded,
  getPoliciesInsuredRisksFilterOptionsFailed,

  getRisksHousingStocksFilterOptions,
  getRisksHousingStocksFilterOptionsSucceeded,
  getRisksHousingStocksFilterOptionsFailed,

  getRisksOrganizationsFilterOptions,
  getRisksOrganizationsFilterOptionsSucceeded,
  getRisksOrganizationsFilterOptionsFailed,

  getRisksCompanyLabelsFilterOptions,
  getRisksCompanyLabelsFilterOptionsSucceeded,
  getRisksCompanyLabelsFilterOptionsFailed,

  getRisksCompanyCodesFilterOptions,
  getRisksCompanyCodesFilterOptionsSucceeded,
  getRisksCompanyCodesFilterOptionsFailed,

  getRisksEconomicUnitsFilterOptions,
  getRisksEconomicUnitsFilterOptionsSucceeded,
  getRisksEconomicUnitsFilterOptionsFailed
} = filtersSlice.actions;

export default filtersSlice.reducer;
